import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User, AuthProvider } from '@/types/auth'
import { logError } from '@/services/errorLogger'
import {
  signInWithGoogle,
  signInWithApple,
  signOut as firebaseSignOut,
  checkRedirectResult,
  onAuthChange,
  isFirebaseConfigured,
  checkAndClearPendingRedirect,
  clearPendingRedirect,
  getCurrentUser,
} from '@/lib/firebase'
import { syncUserProfile } from '@/services/api'

interface AuthState {
  // Auth state
  isAuthenticated: boolean
  isLoading: boolean
  user: User | null
  hasPaid: boolean
  analysisCount: number
  error: string | null

  // Session ID for anonymous users
  sessionId: string | null

  // Demo mode for design review (bypasses payment check)
  demoMode: boolean

  // Actions
  signIn: (provider: AuthProvider) => Promise<boolean>
  signOut: () => Promise<void>
  setPaymentComplete: () => void
  setHasPaid: (hasPaid: boolean) => void
  setError: (error: string | null) => void
  setLoading: (isLoading: boolean) => void
  setSessionId: (sessionId: string) => void
  setUser: (user: User | null) => void
  setDemoMode: (enabled: boolean) => void

  // Backend sync
  syncWithBackend: () => Promise<void>

  // Initialize auth (check redirect result, listen for changes)
  // Returns true if redirect auth was detected (mobile OAuth flow)
  initAuth: () => Promise<boolean>

  // Reset
  resetAuth: () => void
}

// Generate a random session ID
function generateSessionId(): string {
  return `session_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`
}

// Flag to prevent auth listener from clearing state during redirect check
// This is critical: Firebase fires onAuthStateChanged with null BEFORE loading redirect result
let isCheckingRedirect = false

// Flag to prevent initAuth from running multiple times (React Strict Mode causes double-mount)
let initAuthCalled = false
let initAuthPromise: Promise<boolean> | null = null

// Classify Firebase auth errors into user-friendly messages (exported for future use)
export function getAuthErrorMessage(error: unknown): string {
  const errorCode = (error as { code?: string })?.code || ''
  const errorMessage = (error as { message?: string })?.message || ''

  switch (errorCode) {
    case 'auth/popup-closed-by-user':
      return 'Sign-in was cancelled. Please try again.'
    case 'auth/popup-blocked':
      return 'Pop-up was blocked. Please allow pop-ups for this site.'
    case 'auth/cancelled-popup-request':
      return 'Sign-in was cancelled. Please try again.'
    case 'auth/network-request-failed':
      return 'Network error. Please check your internet connection.'
    case 'auth/user-disabled':
      return 'This account has been disabled.'
    case 'auth/account-exists-with-different-credential':
      return 'An account already exists with this email. Try a different sign-in method.'
    case 'auth/operation-not-allowed':
      return 'This sign-in method is not enabled. Please contact support.'
    case 'auth/invalid-credential':
      return 'Invalid credentials. Please try again.'
    default:
      // Check for "not configured" error
      if (errorMessage.includes('not configured')) {
        return 'Sign-in is not available yet. Please try again later.'
      }
      return 'Sign-in failed. Please try again.'
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      hasPaid: false,
      analysisCount: 0,
      error: null,
      sessionId: null,
      demoMode: false,

      signIn: async (provider) => {
        set({ isLoading: true, error: null })

        try {
          let user: User

          if (provider === 'google') {
            user = await signInWithGoogle()
          } else {
            user = await signInWithApple()
          }

          set({
            isAuthenticated: true,
            isLoading: false,
            user,
            error: null,
          })

          // Sync with backend (non-blocking)
          get().syncWithBackend()

          return true // Success
        } catch (error) {
          console.error('[AuthStore] Sign-in error:', error)

          // Check if it's a redirect (not an error)
          const errorMessage = (error as Error)?.message || ''
          if (errorMessage.includes('Redirecting')) {
            // Don't show error for redirect
            return false
          }

          const errorCode = (error as { code?: string })?.code || 'unknown'
          logError('FIREBASE_AUTH', errorMessage, `provider=${provider}, code=${errorCode}`)

          set({
            isLoading: false,
            error: getAuthErrorMessage(error),
          })

          return false // Failed
        }
      },

      signOut: async () => {
        try {
          await firebaseSignOut()
        } catch (error) {
          console.error('[AuthStore] Sign-out error:', error)
        }

        set({
          isAuthenticated: false,
          user: null,
          hasPaid: false,
          error: null,
        })
      },

      setPaymentComplete: () =>
        set({ hasPaid: true }),

      setHasPaid: (hasPaid) =>
        set({ hasPaid }),

      setError: (error) =>
        set({ error, isLoading: false }),

      setLoading: (isLoading) =>
        set({ isLoading }),

      setSessionId: (sessionId) =>
        set({ sessionId }),

      setUser: (user) =>
        set({
          user,
          isAuthenticated: !!user,
          isLoading: false,
        }),

      setDemoMode: (enabled) =>
        set({ demoMode: enabled }),

      syncWithBackend: async () => {
        const { user, isAuthenticated } = get()
        if (!isAuthenticated || !user) return

        try {
          console.log('[AuthStore] Syncing with backend...')
          const profile = await syncUserProfile({
            email: user.email || '',
            displayName: user.displayName || '',
            photoURL: user.photoURL || undefined,
          })

          // Defensive: only update if profile has expected fields
          if (profile && typeof profile.hasPaid === 'boolean') {
            set({
              hasPaid: profile.hasPaid,
              analysisCount: profile.analysisCount || 0,
            })
            console.log('[AuthStore] Synced with backend, hasPaid:', profile.hasPaid)
          } else {
            console.warn('[AuthStore] Backend returned unexpected profile format:', profile)
          }
        } catch (error) {
          console.error('[AuthStore] Backend sync failed:', error)
          // Don't block auth flow if backend sync fails - silently ignore
        }
      },

      initAuth: async () => {
        // Prevent multiple calls (React Strict Mode causes double-mount)
        // getRedirectResult() can only return the result ONCE - subsequent calls return null
        if (initAuthCalled && initAuthPromise) {
          console.log('[AuthStore] initAuth already called, returning existing promise')
          return initAuthPromise
        }
        initAuthCalled = true

        const runInitAuth = async (): Promise<boolean> => {
        console.log('[AuthStore] ========== initAuth START ==========')

        // Generate session ID if not present
        const state = get()
        if (!state.sessionId) {
          set({ sessionId: generateSessionId() })
        }

        // Skip if Firebase not configured
        if (!isFirebaseConfigured()) {
          console.log('[AuthStore] Firebase not configured, skipping auth init')
          return false
        }

        // Check if we were in the middle of a redirect login
        const wasPendingRedirect = checkAndClearPendingRedirect()
        console.log('[AuthStore] wasPendingRedirect:', wasPendingRedirect)
        console.log('[AuthStore] Persisted state - isAuthenticated:', get().isAuthenticated, 'user:', get().user?.email || 'null')
        console.log('[AuthStore] localStorage pending-redirect:', localStorage.getItem('k-skin-pending-redirect'))

        // Check Firebase current user immediately
        const immediateUser = getCurrentUser()
        console.log('[AuthStore] Firebase getCurrentUser() immediately:', immediateUser?.email || 'null')

        // CRITICAL: Set flag to prevent auth listener from clearing state during redirect check
        // Firebase fires onAuthStateChanged with null BEFORE loading the redirect result
        if (wasPendingRedirect) {
          isCheckingRedirect = true
          console.log('[AuthStore] Setting isCheckingRedirect = true to prevent early clear')
        }

        // Track if we found a user through any method
        let foundUser = false

        // Set up permanent auth state listener FIRST
        // This captures auth state changes including redirect results
        onAuthChange((user) => {
          console.log('[AuthStore] Auth listener fired, user:', user?.email || 'null', 'isCheckingRedirect:', isCheckingRedirect)
          if (user) {
            const wasAuthenticated = get().isAuthenticated
            set({
              isAuthenticated: true,
              user,
              isLoading: false,
            })
            clearPendingRedirect()
            isCheckingRedirect = false // Safe to clear flag now
            if (!wasAuthenticated) {
              get().syncWithBackend()
            }
          } else {
            // User is null - but DON'T clear if we're still checking redirect
            // Firebase fires null BEFORE loading redirect result
            if (isCheckingRedirect) {
              console.log('[AuthStore] Ignoring null user during redirect check')
              return
            }
            // Only clear auth state if we thought we were authenticated
            // This handles logout and session expiry
            if (get().isAuthenticated) {
              console.log('[AuthStore] Clearing stale auth state')
              set({
                isAuthenticated: false,
                user: null,
              })
            }
          }
        })

        // If we had a pending redirect, wait a bit for auth to settle
        if (wasPendingRedirect) {
          console.log('[AuthStore] Waiting for redirect auth to settle...')

          // Wait for auth listener to potentially fire
          await new Promise(resolve => setTimeout(resolve, 1000))

          // Check current state
          if (get().isAuthenticated && get().user) {
            console.log('[AuthStore] Auth detected after wait')
            foundUser = true
          }
        }

        // Also try getRedirectResult (some browsers need this)
        if (!foundUser) {
          console.log('[AuthStore] Trying getRedirectResult()...')
          try {
            const redirectUser = await checkRedirectResult()
            console.log('[AuthStore] getRedirectResult returned:', redirectUser?.email || 'null')
            if (redirectUser) {
              console.log('[AuthStore] Got redirect result, user:', redirectUser.email)
              clearPendingRedirect()
              set({
                isAuthenticated: true,
                user: redirectUser,
                isLoading: false,
              })
              get().syncWithBackend()
              foundUser = true
            }
          } catch (e) {
            console.error('[AuthStore] checkRedirectResult error:', e)
          }
        }

        // If still no user but we had a pending redirect, try getCurrentUser
        if (!foundUser && wasPendingRedirect) {
          const currentUser = getCurrentUser()
          if (currentUser) {
            console.log('[AuthStore] getCurrentUser found:', currentUser.email)
            clearPendingRedirect()
            set({
              isAuthenticated: true,
              user: currentUser,
              isLoading: false,
            })
            get().syncWithBackend()
            foundUser = true
          }
        }

        // Extended polling for slow Android devices
        if (!foundUser && wasPendingRedirect) {
          console.log('[AuthStore] Extended polling for auth (8 attempts, 500ms each)...')
          for (let i = 0; i < 8; i++) {
            await new Promise(resolve => setTimeout(resolve, 500))

            // Check if auth listener already set the state
            const currentState = get()
            console.log(`[AuthStore] Poll ${i + 1}/8: isAuthenticated=${currentState.isAuthenticated}, user=${currentState.user?.email || 'null'}`)

            if (currentState.isAuthenticated && currentState.user) {
              console.log('[AuthStore] Auth detected during polling (attempt ' + (i + 1) + ')')
              foundUser = true
              break
            }

            // Try getCurrentUser
            const delayedUser = getCurrentUser()
            console.log(`[AuthStore] Poll ${i + 1}/8: Firebase getCurrentUser=${delayedUser?.email || 'null'}`)
            if (delayedUser) {
              console.log('[AuthStore] Delayed getCurrentUser found:', delayedUser.email)
              clearPendingRedirect()
              set({
                isAuthenticated: true,
                user: delayedUser,
                isLoading: false,
              })
              get().syncWithBackend()
              foundUser = true
              break
            }
          }
        }

        // If we had a pending redirect but still no user, set polling flag
        if (wasPendingRedirect && !foundUser) {
          console.log('[AuthStore] Redirect pending but no user found, enabling polling')
          localStorage.setItem('k-skin-check-auth', 'true')
        }

        // CRITICAL: Clear the redirect check flag after all checks are done
        if (wasPendingRedirect) {
          console.log('[AuthStore] Clearing isCheckingRedirect flag')
          isCheckingRedirect = false

          // If no user was found after all checks, clear stale Zustand state
          // We blocked the auth listener's null event during checking, so we must clear manually
          if (!foundUser && get().isAuthenticated) {
            console.log('[AuthStore] Redirect check complete, no user found, clearing stale state')
            set({
              isAuthenticated: false,
              user: null,
            })
          }
        }

        console.log('[AuthStore] initAuth complete, foundUser:', foundUser)
        return foundUser
        }

        initAuthPromise = runInitAuth()
        return initAuthPromise
      },

      resetAuth: () =>
        set({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          hasPaid: false,
          analysisCount: 0,
          error: null,
          sessionId: generateSessionId(),
        }),
    }),
    {
      name: 'k-skin-auth',
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        hasPaid: state.hasPaid,
        analysisCount: state.analysisCount,
        sessionId: state.sessionId,
        demoMode: state.demoMode,
      }),
      onRehydrateStorage: () => (state) => {
        // Generate session ID if not present
        if (state && !state.sessionId) {
          state.sessionId = generateSessionId()
        }
      },
    }
  )
)
